import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { WorkspaceMenuItem, WorkspaceMenuSection } from '../../types/workspaceMenu';

interface WorkspaceMenuBarProps {
  sections: WorkspaceMenuSection[];
  compact?: boolean;
}

function renderMenuItems(
  items: WorkspaceMenuItem[],
  onClose: () => void,
): React.ReactNode[] {
  return items.map((item, index) => {
    if (item.kind === 'divider') {
      return <Divider key={`div-${index}`} sx={{ my: 0.5 }} />;
    }
    const row = (
      <MenuItem
        key={item.id}
        disabled={item.disabled}
        onClick={() => {
          if (item.disabled) return;
          item.onClick();
          onClose();
        }}
        dense
      >
        {item.icon ? (
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
        ) : null}
        <ListItemText primary={item.label} />
      </MenuItem>
    );
    if (!item.helpTooltip) return row;
    return (
      <Tooltip key={item.id} title={item.helpTooltip} placement="right">
        <span style={{ display: 'block' }}>{row}</span>
      </Tooltip>
    );
  });
}

const WorkspaceMenuBar: React.FC<WorkspaceMenuBarProps> = ({ sections, compact = false }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const menuBarRef = useRef<HTMLDivElement>(null);
  const menuPaperRef = useRef<HTMLDivElement>(null);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileAnchor, setMobileAnchor] = useState<null | HTMLElement>(null);

  const visibleSections = useMemo(
    () => sections.filter((s) => s.items.length > 0),
    [sections],
  );

  const closeSectionMenu = () => {
    setOpenSectionId(null);
    setAnchorEl(null);
  };

  const openSectionMenu = (sectionId: string, el: HTMLElement) => {
    setOpenSectionId(sectionId);
    setAnchorEl(el);
  };

  const handleSectionButtonActivate = (sectionId: string, button: HTMLButtonElement) => {
    if (openSectionId === sectionId) {
      closeSectionMenu();
      return;
    }
    openSectionMenu(sectionId, button);
  };

  const handleMenuBarMouseDownCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const button = (e.target as HTMLElement).closest('[data-workspace-menu-section]');
    if (!(button instanceof HTMLButtonElement)) return;
    const sectionId = button.getAttribute('data-workspace-menu-section');
    if (!sectionId) return;
    e.preventDefault();
    handleSectionButtonActivate(sectionId, button);
  };

  useEffect(() => {
    if (!openSectionId) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        closeSectionMenu();
        return;
      }
      if (menuBarRef.current?.contains(target)) return;
      if (menuPaperRef.current?.contains(target)) return;
      closeSectionMenu();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [openSectionId]);

  const handleSectionButtonMouseEnter = (
    sectionId: string,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (openSectionId !== null && openSectionId !== sectionId) {
      openSectionMenu(sectionId, e.currentTarget);
    }
  };

  const handleMenuClose = (
    _event: MouseEvent | TouchEvent | React.SyntheticEvent,
    reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
  ) => {
    if (reason === 'backdropClick') {
      return;
    }
    closeSectionMenu();
  };

  const menuButtonSx = {
    color: 'inherit',
    textTransform: 'none' as const,
    minWidth: 0,
    px: compact ? 1 : 1.25,
    py: 0.25,
    fontSize: compact ? '0.85rem' : '0.95rem',
    fontWeight: 500,
    borderRadius: 1,
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  };

  if (narrow) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Button
          startIcon={<MenuIcon fontSize="small" />}
          sx={menuButtonSx}
          onClick={(e) => setMobileAnchor(e.currentTarget)}
        >
          Menu
        </Button>
        <Menu
          anchorEl={mobileAnchor}
          open={Boolean(mobileAnchor)}
          onClose={() => setMobileAnchor(null)}
          PaperProps={{ sx: { minWidth: 280, maxHeight: '70vh' } }}
        >
          {visibleSections.map((section, sectionIndex) => (
            <Box key={section.id}>
              {sectionIndex > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
              <ListSubheader sx={{ lineHeight: 2, fontWeight: 700 }}>
                {section.label}
              </ListSubheader>
              {renderMenuItems(section.items, () => setMobileAnchor(null))}
            </Box>
          ))}
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      ref={menuBarRef}
      onMouseDownCapture={handleMenuBarMouseDownCapture}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.25,
        minWidth: 0,
        rowGap: 0.25,
      }}
    >
      {visibleSections.map((section) => (
        <Button
          key={section.id}
          data-workspace-menu-section={section.id}
          sx={menuButtonSx}
          aria-haspopup="true"
          aria-expanded={openSectionId === section.id}
          onMouseEnter={(e) => handleSectionButtonMouseEnter(section.id, e)}
        >
          {section.label}
        </Button>
      ))}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl && openSectionId)}
        onClose={handleMenuClose}
        disableRestoreFocus
        disableScrollLock
        hideBackdrop
        slotProps={{
          root: {
            sx: { pointerEvents: 'none' },
          },
        }}
        MenuListProps={{ autoFocusItem: true, dense: true }}
        PaperProps={{
          ref: menuPaperRef,
          elevation: 8,
          sx: (theme) => {
            const isDark = theme.palette.mode === 'dark';
            return {
              pointerEvents: 'auto',
              minWidth: 280,
              maxHeight: 480,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${
                isDark ? alpha(theme.palette.common.white, 0.14) : theme.palette.divider
              }`,
              boxShadow: isDark
                ? `0 10px 40px ${alpha(theme.palette.common.black, 0.55)}`
                : theme.shadows[10],
            };
          },
        }}
      >
        {renderMenuItems(
          visibleSections.find((s) => s.id === openSectionId)?.items ?? [],
          closeSectionMenu,
        )}
      </Menu>
    </Box>
  );
};

export default WorkspaceMenuBar;
